import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { MapsAPILoader } from '@agm/core';
import { MapService } from '../../services';
import {
  InteractiveMapData,
  InteractiveMapIcon,
  InteractiveMapTask,
} from '../../interfaces/interactive-map';

declare var google: any;

@Component({
  selector: 'map-control-v2',
  templateUrl: './map-control-v2.component.html',
  styleUrls: ['./map-control-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapControlV2Component implements OnChanges {
  @Input() taskIds: number[];
  @Input() routeId: number;
  @Input() selectedTaskId: number;
  @Input() displayTaskLegends: boolean;
  @Input() displayAdditionalLegendLabels: boolean = true;

  @Output() selectedTaskIdChange = new EventEmitter<number>();
  @Output() mapClicked = new EventEmitter<google.maps.MouseEvent>();
  @Output() taskClicked = new EventEmitter<InteractiveMapTask>();
  @Output() mapLoaded = new EventEmitter<void>();

  public isLoading = false;
  public mapData: InteractiveMapData = null;
  public mapBounds: google.maps.LatLngBounds;

  private nativeMap: google.maps.Map;

  public initialLat = 54.5;
  public initialLng = -2.5;
  public initialZoom = 6;

  constructor(
    private mapService: MapService,
    private mapsApiLoader: MapsAPILoader,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.routeId || changes.selectedTaskId || changes.taskIds) {
      void this.fetchMapData();
    }
  }

  private async fetchMapData(): Promise<void> {
    this.isLoading = true;
    this.mapData = null;
    this.cdr.markForCheck();

    try {
      await this.mapsApiLoader.load();

      let data: InteractiveMapData;
      if (this.taskIds && this.taskIds.length > 0) {
        data = await this.mapService.getInteractiveMapDataForTasks(
          this.taskIds,
          this.selectedTaskId,
        );
      } else if (this.routeId) {
        data = await this.mapService.getInteractiveMapDataForRoute(
          this.routeId,
          this.selectedTaskId,
        );
      }

      this.calculateBounds(data?.tasks);
      this.mapData = data;
    } catch (error) {
      console.error('Failed to fetch interactive map data', error);
      this.mapData = null;
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  public onMapReady(map: google.maps.Map): void {
    this.nativeMap = map;

    if (this.nativeMap && this.mapBounds) {
      google.maps.event.trigger(this.nativeMap, 'resize');
      this.nativeMap.fitBounds(this.mapBounds, 50);
    }

    this.mapLoaded.emit();
  }

  private calculateBounds(tasks: InteractiveMapTask[]): void {
    if (tasks && tasks.length > 0) {
      this.mapBounds = new google.maps.LatLngBounds();

      for (const task of tasks) {
        this.mapBounds.extend(
          new google.maps.LatLng(task.pickupLocation.latitude, task.pickupLocation.longitude),
        );

        this.mapBounds.extend(
          new google.maps.LatLng(task.dropoffLocation.latitude, task.dropoffLocation.longitude),
        );
      }
    }
  }

  public getMarkerIcon(icon: InteractiveMapIcon): google.maps.Icon | null {
    if (!icon?.url) {
      return null;
    }

    return { url: icon.url };
  }

  public onMapClicked(event: google.maps.MouseEvent): void {
    this.mapClicked.emit(event);
  }

  public onTaskClicked(task: InteractiveMapTask): void {
    this.selectedTaskId = task.taskId;
    this.selectedTaskIdChange.emit(this.selectedTaskId);
    this.taskClicked.emit(task);
    void this.fetchMapData();
  }

  public get mapDataHasPrimaryTask(): boolean {
    return this.mapData?.tasks?.some((task) => task.isPrimary) ?? false;
  }

  public toggleRoute(task: InteractiveMapTask): void {
    this.selectedTaskId = this.selectedTaskId === task.taskId ? undefined : task.taskId;
    this.selectedTaskIdChange.emit(this.selectedTaskId);
    this.taskClicked.emit(task);
    void this.fetchMapData();
  }
}
